/**
 * Display-only helpers for showing wear extension instead of raw absolute
 * measurements (#6032, #5547, #9640).
 *
 * IMPORTANT: these helpers are for DISPLAY ONLY. Stored observation values
 * (Measurement, CalculatedPitchGauge, CalculatedTapePitchExtension) remain
 * RAW ABSOLUTE values — the Word report, Integration API and Admin fleet
 * reports subtract the baseline downstream (see PR #9929).
 *
 * Settled calculation:
 *   extension mm = storedCalculatedValue − partDimension.baselineValue
 *   wear%        = extension ÷ partDimension.limit × 100
 *   invalid (≤ 0 / missing) limit ⇒ NO percentage (never substitute a divisor)
 */

export interface WearDisplayDimension {
  baselineValue: number;
  limit: number;
  wearDirection?: number;
}

/** Mirrors the server WearDirection enum (#10233): 1 = Increasing, 2 = Decreasing, -1 = Unknown. */
export const WEAR_DIRECTION_DECREASING = 2;

const isDecreasing = (dimension: WearDisplayDimension): boolean =>
  dimension.wearDirection === WEAR_DIRECTION_DECREASING;

const mm = (value: number): string => value.toFixed(1);

class WearDisplayHelpers {
  /** True when the dimension's limit gives a usable wear span (see ActionStatusHelpers.calculateWearPercentage). */
  static hasValidLimit(dimension?: WearDisplayDimension | null): boolean {
    if (!dimension) return false;
    return isDecreasing(dimension)
      ? dimension.baselineValue - dimension.limit > 0
      : dimension.limit > 0;
  }

  /**
   * Readings from new (0% wear) to condemned (100% wear), lowest first.
   * Increasing / Unknown: baseline to baseline + limit (limit is the allowed growth).
   * Decreasing (#10233): limit to baseline (limit is the absolute condemning reading).
   */
  static getReadingRange(dimension: WearDisplayDimension): {
    min: number;
    max: number;
  } {
    return isDecreasing(dimension)
      ? { min: dimension.limit, max: dimension.baselineValue }
      : {
          min: dimension.baselineValue,
          max: dimension.baselineValue + dimension.limit,
        };
  }

  /** Wear in mm: how far the reading has moved from the baseline in the wear direction. */
  static calculateWearMm(
    value: number,
    dimension: WearDisplayDimension,
  ): number {
    return isDecreasing(dimension)
      ? dimension.baselineValue - value
      : value - dimension.baselineValue;
  }

  /**
   * The value to display for a calculated pitch field (2 dp).
   * With a baseline on record: extension = value − baseline.
   * Without one: the raw value (degraded case — caller shows an explanatory note).
   */
  static formatExtensionDisplayValue({
    average,
    dimension,
  }: {
    average: number;
    dimension?: WearDisplayDimension | null;
  }): string {
    if (!dimension) return average.toFixed(2);
    return (average - dimension.baselineValue).toFixed(2);
  }

  /**
   * Explanatory label under the calculated extension value, phrased for field
   * inspectors: result first, plain words, one idea per line. Rendered as up to
   * three short lines (join with a newline; the component uses whitespace-pre-line):
   *   Extension (wear): 3.0 mm
   *   average reading 595.0 mm − new-part baseline 592.0 mm
   *   Condemn at 12.0 mm wear (reading up to 604.0 mm)
   *
   * The only minus sign is the genuine subtraction on the middle line; ranges use
   * "to"/"up to" so nothing else reads as another minus.
   */
  static formatExtensionLabel({
    average,
    dimension,
  }: {
    average: number;
    dimension?: WearDisplayDimension | null;
  }): string {
    if (!dimension) {
      return 'No new-part baseline on record — showing raw measurement';
    }

    const extension = average - dimension.baselineValue;
    const result = `Extension (wear): ${mm(extension)} mm`;
    const breakdown = `average reading ${mm(average)} mm − new-part baseline ${mm(dimension.baselineValue)} mm`;

    if (!WearDisplayHelpers.hasValidLimit(dimension)) {
      return `${result}\n${breakdown}\nLimit not on record — no wear range`;
    }

    const rangeMax = dimension.baselineValue + dimension.limit;
    const condemn = `Condemn at ${mm(dimension.limit)} mm wear (reading up to ${mm(rangeMax)} mm)`;
    return `${result}\n${breakdown}\n${condemn}`;
  }

  /**
   * Persistent helper text under a measurement input, shown BEFORE the user
   * types, e.g. `Expected reading: 480.0 to 592.0 mm (new-part baseline 480.0 + limit 112.0)`,
   * or for a decreasing dimension `Expected reading: 270.0 to 258.0 mm (new-part baseline 270.0, worn limit 258.0)`
   */
  static formatAcceptableRangeHelperText({
    dimension,
  }: {
    dimension?: WearDisplayDimension | null;
  }): string {
    if (!dimension) {
      return 'No new-part baseline on record — range unavailable';
    }

    if (!WearDisplayHelpers.hasValidLimit(dimension)) {
      return `New-part baseline ${mm(dimension.baselineValue)} mm — limit not on record, range unavailable`;
    }

    if (isDecreasing(dimension)) {
      return `Expected reading: ${mm(dimension.baselineValue)} to ${mm(dimension.limit)} mm (new-part baseline ${mm(dimension.baselineValue)}, worn limit ${mm(dimension.limit)})`;
    }

    const rangeMax = dimension.baselineValue + dimension.limit;
    return `Expected reading: ${mm(dimension.baselineValue)} to ${mm(rangeMax)} mm (new-part baseline ${mm(dimension.baselineValue)} + limit ${mm(dimension.limit)})`;
  }

  /**
   * Preview of what a typed-but-not-yet-added pitch gauge reading would do to
   * the committed average (#10668), e.g. `{ average: '611.0 mm',
   * extension: '1.0 mm' }` for "Average would be 611.0 mm, extension 1.0 mm".
   *
   * The parts come back separately so the caller can emphasise the figures
   * inside its own sentence; the formatting rules (1 dp, unit) stay here.
   *
   * The pending reading is NOT committed — this is the same display-only
   * arithmetic as formatExtensionLabel, run on `committed + [pending]` so the
   * inspector can see the consequence before tapping Add measurement.
   * Without a baseline on record there is no extension to state, so `extension`
   * is null (never substitute a baseline).
   */
  static formatPendingPreview({
    committed,
    pending,
    dimension,
  }: {
    committed: number[];
    pending: number;
    dimension?: WearDisplayDimension | null;
  }): { average: string; extension: string | null } {
    const readings = [...committed, pending];
    const average =
      readings.reduce((total, reading) => total + reading, 0) / readings.length;

    return {
      average: `${mm(average)} mm`,
      extension: dimension
        ? `${mm(average - dimension.baselineValue)} mm`
        : null,
    };
  }

  /**
   * Visible rejection message (replaces the previous silent drop), e.g.
   * `595.0 mm is outside the acceptable range 480.0 – 592.0 mm for this part`
   * In percentage entry mode (unit '%') the value and range are formatted in
   * percent: `310.0% is outside the acceptable range 0.0 – 100.0% for this part`.
   */
  static formatOutOfRangeError({
    value,
    minValue,
    maxValue,
    unit = 'mm',
  }: {
    value: number;
    minValue: number;
    maxValue: number;
    /** 'mm' (default) for measurement entry; '%' for percentage entry mode. */
    unit?: 'mm' | '%';
  }): string {
    if (unit === '%') {
      return `${mm(value)}% is outside the acceptable range ${mm(minValue)} – ${mm(maxValue)}% for this part`;
    }
    return `${mm(value)} mm is outside the acceptable range ${mm(minValue)} – ${mm(maxValue)} mm for this part`;
  }

  /**
   * Action status display values (#10204): the bar previously rendered the raw
   * floats from ActionStatusBarModel, e.g. `89% (10.670000000000016mm)`.
   * Percentage shows as a whole number (matching InlineActionStatus) and the
   * wear measurement to 2 dp (matching the calculated extension displays).
   * The underlying model values stay raw.
   */
  static formatActionStatusPercentage(percentage: number): string {
    return percentage.toFixed(0);
  }

  static formatActionStatusWear(measurement: number): string {
    return measurement.toFixed(2);
  }
}

export default WearDisplayHelpers;
